'use client';
/* eslint-disable tailwindcss/no-custom-classname */
import classNames from 'classnames';
import { useState } from 'react';
import { CollapsibleSection } from '../../components/CollapsibleSection/CollapsibleSection';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { TRANSLATIONS } from './i18n';
import { ComponentScenarioAccordion, ScenarioAccordionItem } from './types';

const MIN_SCENARIOS = 2;

const isRenderableScenario = (scenario: ScenarioAccordionItem): boolean =>
  !!scenario.emoji?.trim() &&
  !!scenario.scenarioText?.trim() &&
  !!scenario.answerText?.trim();

export const ScenarioAccordion = ({
  moduleTitle,
  scenarios,
  pushDataLayer,
  componentIndex,
}: ComponentScenarioAccordion): JSX.Element | null => {
  const { brand, lang } = useCmsClientContext();
  const t = getClientTranslations(TRANSLATIONS, { brand, lang });

  const visibleScenarios = scenarios.filter(isRenderableScenario);

  // First scenario is expanded by default on page load.
  const [openId, setOpenId] = useState<string | null>(
    visibleScenarios[0]?.id ?? null,
  );

  // Module does not render when fewer than 2 scenarios.
  if (visibleScenarios.length < MIN_SCENARIOS) {
    return null;
  }

  const title = moduleTitle?.trim() || t.scenario_accordion_module_title;

  const handleToggle = (id: string) => {
    // Only one accordion can be expanded at a time.
    setOpenId(id || null);
  };

  return (
    <section
      data-component="scenario-accordion"
      className="w-full rounded-radius/card border border-borders/default p-spacing/margin/x"
    >
      <div className="flex flex-col gap-spacing/padding/y">
        <h2 className="mhg-alpha-typography-display-1 text-text/primary">
          {title}
        </h2>

        <div className="flex flex-col gap-spacing/gap/soft-12px">
          {visibleScenarios.map((scenario, scenarioIndex) => {
            const isOpen = openId === scenario.id;
            const scenarioTitle = scenario.scenarioText?.trim() ?? '';
            const linkLabel = scenario.textLinkLabel?.trim() ?? '';
            const showLink =
              !!scenario.textLinkEnabled &&
              !!linkLabel &&
              !!scenario.textLinkUrl?.trim();

            return (
              <CollapsibleSection
                key={scenario.id}
                id={scenario.id}
                isOpen={isOpen}
                onToggle={(nextId) => {
                  // `isOpen` is the pre-toggle state, so it names the action
                  // this click performs, not the one it lands in (GA #2211,
                  // CMS-1693).
                  pushDataLayer?.({
                    event: 'navigate',
                    component_type: 'accordion',
                    component_title: `${title}_${scenarioTitle}`,
                    component_index: componentIndex ?? '',
                    action: isOpen ? 'collapse' : 'expand',
                    index: String(scenarioIndex),
                  });
                  handleToggle(nextId);
                }}
                className="overflow-hidden rounded-radius/button bg-fill/card"
                buttonClassName={classNames('py-spacing/gap/soft-12px px-spacing/padding/soft-12px', {
                  '!bg-fill/primary-light': isOpen,
                  '!bg-fill/alternative-1': !isOpen,
                })}
                content={
                  <div className="flex flex-col gap-spacing/gap/soft-6px px-spacing/padding/soft-12px py-spacing/gap/soft-12px">
                    <p className="mhg-alpha-typography-body-2-blog text-text/secondary">
                      {scenario.answerText}
                    </p>
                    {showLink && (
                      <a
                        href={scenario.textLinkUrl}
                        onClick={() => {
                          // `/` here but `_` in #2211 above: the two payloads
                          // differ that way in the AirTable spec, kept
                          // verbatim (GA #2212, CMS-1693).
                          pushDataLayer?.({
                            event: 'navigate',
                            component_type: 'text-link',
                            component_title: `${title}/${scenarioTitle}_${linkLabel}`,
                            component_index: componentIndex ?? '',
                            action: 'click',
                            index: String(scenarioIndex),
                          });
                        }}
                        className="mhg-alpha-typography-button-small w-fit text-fill/primary underline"
                      >
                        {scenario.textLinkLabel}
                      </a>
                    )}
                  </div>
                }
              >
                <span aria-hidden className="shrink-0">
                  {scenario.emoji}
                </span>
                <span
                  className={classNames(
                    'mhg-alpha-typography-bullet-1 min-w-0',
                    isOpen ? 'text-fill/primary-dark' : 'text-text/primary',
                  )}
                >
                  {`"${scenario.scenarioText}"`}
                </span>
              </CollapsibleSection>
            );
          })}
        </div>
      </div>
    </section>
  );
};
